// iftar — i01 Iftor / Ramadan iftor gathering catalog card.
//
// Mood: dusk-warm sand paper with deep brown accents. Hero motif is a
// crescent moon paired with a slim minaret silhouette, set inside a
// quiet circular medallion. The composition stays restrained and
// respectful. Category eyebrow in Uzbek: "IFTAR".
import { type LayoutCtx } from "./_shared";

export function IftarLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
}: LayoutCtx) {
  // Sand-bronze foil — warm, soft, golden hour.
  const FOIL =
    "linear-gradient(135deg, #6E4818 0%, #A26B2A 25%, #D6A85E 50%, #A26B2A 75%, #6E4818 100%)";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
          position: "relative",
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          IFTAR
        </div>

        {/* Crescent moon + minaret medallion */}
        <div
          style={{
            width: "60%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <svg
            viewBox="0 0 120 120"
            style={{ width: "100%", height: "100%", display: "block" }}
            aria-hidden
          >
            <defs>
              <radialGradient id="ift-dusk" cx="0.5" cy="0.6" r="0.7">
                <stop offset="0%" stopColor={accent} stopOpacity="0.15" />
                <stop offset="100%" stopColor={accent} stopOpacity="0.0" />
              </radialGradient>
            </defs>

            {/* Soft dusk wash */}
            <circle cx="60" cy="60" r="54" fill="url(#ift-dusk)" />

            {/* Double hairline ring */}
            <circle cx="60" cy="60" r="56" fill="none" stroke={accent} strokeOpacity="0.5" strokeWidth="0.8" />
            <circle cx="60" cy="60" r="52" fill="none" stroke={accent} strokeOpacity="0.25" strokeWidth="0.4" />

            {/* Crescent moon, upper-left */}
            <path
              d="M50 38 a18 18 0 1 1 -10 -16 a13 13 0 1 0 10 16 z"
              fill={accent}
              fillOpacity="0.9"
            />

            {/* Star at moon tip */}
            <g transform="translate(50 24)">
              <path
                d="M0 -3 L0.8 -0.8 L3 0 L0.8 0.8 L0 3 L-0.8 0.8 L-3 0 L-0.8 -0.8 Z"
                fill={accent}
                fillOpacity="0.85"
              />
            </g>

            {/* Minaret silhouette, lower-right */}
            <g transform="translate(80 96)" fill={accent} fillOpacity="0.8">
              {/* Base */}
              <rect x="-3" y="-20" width="6" height="20" />
              {/* Shaft */}
              <rect x="-2" y="-44" width="4" height="24" />
              {/* Balcony */}
              <rect x="-4" y="-46" width="8" height="2" />
              {/* Roof / dome */}
              <path d="M-2 -50 Q 0 -54 2 -50 L 2 -46 L -2 -46 Z" />
              {/* Spire */}
              <rect x="-0.4" y="-58" width="0.8" height="8" />
              <circle cx="0" cy="-58" r="0.8" />
            </g>

            {/* Horizon line */}
            <line
              x1="14"
              y1="92"
              x2="106"
              y2="92"
              stroke={accent}
              strokeOpacity="0.4"
              strokeWidth="0.4"
            />
          </svg>
        </div>

        {/* Names */}
        <div
          style={{
            fontFamily: font,
            fontSize: 30,
            lineHeight: 1.05,
            fontWeight: 400,
            color: ink,
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Bottom caption */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          IFTAR · {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
